import React from 'react';
import { Cartesian3DAxes } from '../common/Cartesian3DAxes.jsx';
import { DraggablePoint } from '../common/DraggablePoint.jsx';

export const CircularConeComponent = ({ params, onParamChange, svgRef, showCoordinates }) => {
  const {
    centerX = 200,
    centerY = 150,
    radius = 80,
    height = 128,
    showHeight = true,
    showRadius = true,
    showSlant = true,
    show3DAxes = true,
    baseFill = "rgba(100,100,100,0.3)",
    sideFill = "rgba(150,150,150,0.4)",
    stroke = "black",
    strokeWidth = 2,
    heightColor = "red",
    radiusColor = "blue",
    slantColor = "green"
  } = params;

  // Calculate cone geometry
  const apexX = centerX;
  const apexY = centerY - height;
  
  // Base ellipse (perspective view)
  const baseRx = radius;
  const baseRy = radius * 0.3; // Perspective compression
  
  // Left and right base points
  const leftBaseX = centerX - radius;
  const leftBaseY = centerY;
  const rightBaseX = centerX + radius;
  const rightBaseY = centerY;
  
  // Calculate slant height
  const slantHeight = Math.sqrt(height * height + radius * radius);
  
  return (
    <g>
      {/* 3D Axes */}
      {show3DAxes && <Cartesian3DAxes centerX={centerX} centerY={centerY + 20} size={60} />}
      
      {/* Base ellipse (bottom) */}
      <ellipse
        cx={centerX}
        cy={centerY}
        rx={baseRx}
        ry={baseRy}
        fill={baseFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Cone side surfaces */}
      <path
        d={`M ${leftBaseX} ${leftBaseY} Q ${apexX - radius * 0.3} ${apexY + height * 0.3} ${apexX} ${apexY}`}
        fill={sideFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <path
        d={`M ${rightBaseX} ${rightBaseY} Q ${apexX + radius * 0.3} ${apexY + height * 0.3} ${apexX} ${apexY}`}
        fill={sideFill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Front base arc */}
      <path
        d={`M ${leftBaseX} ${leftBaseY} A ${baseRx} ${baseRy} 0 0 0 ${rightBaseX} ${rightBaseY}`}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      
      {/* Height line */}
      {showHeight && (
        <>
          <line
            x1={centerX}
            y1={centerY}
            x2={apexX}
            y2={apexY}
            stroke={heightColor}
            strokeWidth={strokeWidth}
            strokeDasharray="5,5"
          />
          <text
            x={centerX + 10}
            y={centerY - height / 2}
            fontSize="12"
            fill={heightColor}
            fontWeight="bold"
          >
            h = {height.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Radius line */}
      {showRadius && (
        <>
          <line
            x1={centerX}
            y1={centerY}
            x2={rightBaseX}
            y2={rightBaseY}
            stroke={radiusColor}
            strokeWidth={strokeWidth}
          />
          <text
            x={centerX + radius / 2}
            y={centerY + 20}
            fontSize="12"
            fill={radiusColor}
            fontWeight="bold"
          >
            r = {radius.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Slant height line */}
      {showSlant && (
        <>
          <line
            x1={rightBaseX}
            y1={rightBaseY}
            x2={apexX}
            y2={apexY}
            stroke={slantColor}
            strokeWidth={strokeWidth}
          />
          <text
            x={apexX + radius * 0.6}
            y={apexY + height * 0.5}
            fontSize="12"
            fill={slantColor}
            fontWeight="bold"
          >
            l = {slantHeight.toFixed(0)}
          </text>
        </>
      )}
      
      {/* Draggable points */}
      {onParamChange && (
        <>
          <DraggablePoint
            cx={centerX}
            cy={centerY}
            label="Center"
            coords={showCoordinates ? `(${centerX}, ${centerY})` : ''}
            onDrag={(newX, newY) => {
              onParamChange('centerX', newX);
              onParamChange('centerY', newY);
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={apexX}
            cy={apexY}
            label="Apex"
            coords={showCoordinates ? `h=${height.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newHeight = centerY - newY;
              onParamChange('height', Math.max(30, Math.min(200, newHeight)));
            }}
            svgRef={svgRef}
          />
          <DraggablePoint
            cx={rightBaseX}
            cy={rightBaseY}
            label="Radius"
            coords={showCoordinates ? `r=${radius.toFixed(0)}` : ''}
            onDrag={(newX, newY) => {
              const newRadius = Math.abs(newX - centerX);
              onParamChange('radius', Math.max(20, Math.min(150, newRadius)));
            }}
            svgRef={svgRef}
          />
        </>
      )}
    </g>
  );
};
